import { Editor, Element, Node, Path, Transforms } from 'slate';

import * as serializer from 'basic-editor-value-serializer';

import { isEmptyParagraph } from './insert';
import { canStackItem } from './keep-in-item';
import { notifyRefused } from './notify';
import { moveItemSettings, wrapInStack } from './wrap';

function findEmptyParagraphReference(editor) {

  const entry = Editor.above(editor, { match: Element.isElement, mode: 'lowest' });

  if (typeof entry === 'undefined' || isEmptyParagraph(editor, entry[0]) === false) {
    return null;
  }

  return Editor.pathRef(editor, entry[1]);

}

// Pasting into an empty paragraph of a layout replaces that paragraph, as it does at the top level.
export function insertReplacingEmptyBlock(editor, nodes) {

  const reference = findEmptyParagraphReference(editor);

  Editor.withoutNormalizing(editor, function insertAndReplace() {

    Transforms.insertNodes(editor, nodes);

    if (reference === null) {
      return;
    }

    const path = reference.unref();

    if (path !== null && Path.isAncestor(path, editor.selection.anchor.path) === false) {
      Transforms.removeNodes(editor, { at: path });
    }

  });

}

function selectEnd(editor, path) {
  Transforms.select(editor, Editor.end(editor, path));
}

function replaceWithOne(editor, path, node) {

  Editor.withoutNormalizing(editor, function replace() {
    Transforms.insertNodes(editor, node, { at: Path.next(path) });
    moveItemSettings(editor, path, Path.next(path));
    Transforms.removeNodes(editor, { at: path });
  });
  selectEnd(editor, path);

}

function replaceWithStack(editor, path, nodes) {

  Editor.withoutNormalizing(editor, function replace() {
    wrapInStack(editor, path);
    Transforms.insertNodes(editor, nodes, { at: path.concat(1) });
    Transforms.removeNodes(editor, { at: path.concat(0) });
  });
  selectEnd(editor, path.concat(nodes.length - 1));

}

function addAfterItem(editor, path, nodes) {

  Editor.withoutNormalizing(editor, function add() {
    Transforms.insertNodes(editor, nodes, { at: Path.next(wrapInStack(editor, path)) });
  });
  selectEnd(editor, path.concat(nodes.length));

}

// Blocks pasted at an item of a row stay in that item's place: they replace it when it is an empty
// paragraph, and otherwise go into an auto stack below it (spec 3.8).
export function pasteIntoRowItem(editor, path, nodes) {

  const isEmpty = isEmptyParagraph(editor, Node.get(editor, path));

  if (isEmpty === true && nodes.length === 1) {
    replaceWithOne(editor, path, nodes[0]);
    return;
  }

  if (canStackItem(editor, path) === false) {
    notifyRefused();
    return;
  }

  if (isEmpty === true) {
    replaceWithStack(editor, path, nodes);
    return;
  }

  addAfterItem(editor, path, nodes);

}

// Paste styles onto a layout also carries the copied layout's settings. Item sizes never travel,
// because they belong to the position, not the block.
export function pasteLayoutSettings(editor, settings, path) {

  const node = Node.get(editor, path);

  if (typeof settings === 'undefined' || node.type !== serializer.types.LAYOUT) {
    return;
  }

  Transforms.setNodes(editor, { properties: { ...node.properties, layout: JSON.parse(JSON.stringify(settings)) } }, { at: path });

}
